import { SignatureRequestEmailPreview } from "@/components/features/matter/file-repo/signature-requests/SignatureRequestEmailPreview";
import { ShareFolderEmailPreview } from "@/components/features/matter/file-repo/share-folders/ShareFolderEmailPreview";
import {
  Form,
  FormSubmitButton,
  SheetFooter,
  Slideover,
  TextFormField,
  Typography,
} from "@/components/ui";
import { FormLoadingOverlay } from "@/components/ui/Form/FormLoadingOverlay";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { useSendTemplatedEmail } from "@/lib/data/react-query/contacts/contact-templated-email";
import type {
  SendTemplatedEmailPayload,
  ShareFolderTemplateData,
  SignatureRequestTemplateData,
} from "@/lib/data/requests/contacts/contact-templated-email";
import { slideoverFieldsetClassName } from "@/lib/utils/slideover-classes";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

interface SendEmailSlideoverProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  title: string;
  description?: string;
  toEmails: string[];
  contactIds: string[];
  templateName: string;
  templateData: SendTemplatedEmailPayload["templateData"];
  defaultCcEmails?: string[];
  defaultBccEmails?: string[];
  onSuccess?: () => void;
}

const SIGNATURE_TEMPLATE_PREFIX = "ElectronicSignatureRequestEmailTemplate";
const SHARE_FOLDER_TEMPLATE_NAME = "ShareFolderNotificationEmailTemplate";

const formSchema = z.object({
  to: z.string().min(1, "Recipient email is required"),
  cc: z.string().optional(),
  bcc: z.string().optional(),
});

type FormSchema = z.infer<typeof formSchema>;

const joinEmails = (emails?: string[]) =>
  (emails ?? []).filter(Boolean).join(", ");

const parseEmails = (value?: string) =>
  value
    ?.split(/[,\n;]/)
    .map((email) => email.trim())
    .filter((email) => email.length > 0);

export const SendEmailSlideover = ({
  open,
  setOpen,
  title,
  description,
  toEmails,
  contactIds,
  templateName,
  templateData,
  defaultCcEmails,
  defaultBccEmails,
  onSuccess,
}: SendEmailSlideoverProps) => {
  const {
    mutateAsync: sendTemplatedEmailMutation,
    isPending,
    isSuccess,
    reset: resetSendTemplatedEmail,
  } = useSendTemplatedEmail();

  const defaultValues = useMemo(
    () => ({
      to: joinEmails(toEmails),
      cc: joinEmails(defaultCcEmails),
      bcc: joinEmails(defaultBccEmails),
    }),
    [toEmails, defaultCcEmails, defaultBccEmails],
  );

  const isSignatureTemplate = templateName.startsWith(
    SIGNATURE_TEMPLATE_PREFIX,
  );
  const isShareFolderTemplate = templateName === SHARE_FOLDER_TEMPLATE_NAME;

  const signatureTemplateData = isSignatureTemplate
    ? (templateData as SignatureRequestTemplateData)
    : null;
  const shareFolderTemplateData = isShareFolderTemplate
    ? (templateData as ShareFolderTemplateData)
    : null;

  const form = useForm<FormSchema>({
    resolver: zodResolver(formSchema),
    defaultValues,
  });

  useEffect(() => {
    if (open) {
      form.reset(defaultValues);
    }
  }, [open, form, defaultValues]);

  useEffect(() => {
    if (isSuccess) {
      onSuccess?.();
      setOpen(false);
      form.reset(defaultValues);
      resetSendTemplatedEmail();
    }
  }, [
    setOpen,
    form,
    defaultValues,
    onSuccess,
    isSuccess,
    resetSendTemplatedEmail,
  ]);

  const handleSubmit = async (data: FormSchema) => {
    const ccEmails = parseEmails(data.cc);
    const bccEmails = parseEmails(data.bcc);

    await sendTemplatedEmailMutation({
      toEmails,
      contactIds,
      templateName,
      templateData,
      ccEmails: ccEmails && ccEmails.length > 0 ? ccEmails : undefined,
      bccEmails: bccEmails && bccEmails.length > 0 ? bccEmails : undefined,
    });
  };

  const isSubmitting = isPending;

  return (
    <Slideover
      title={title}
      description={description}
      open={open}
      setOpen={setOpen}
      isDirty={form.formState.isDirty}
    >
      <Form {...form}>
        <form
          className="relative flex grow flex-col overflow-hidden"
          onSubmit={async (event) => {
            event.preventDefault();
            event.stopPropagation();
            await form.handleSubmit(handleSubmit)();
          }}
        >
          {isSubmitting && <FormLoadingOverlay />}
          <div className="overflow-y-auto">
            <fieldset
              className={`${slideoverFieldsetClassName} my-0 mt-6 grid-cols-1 p-0 px-6`}
            >
              <TextFormField
                label="To"
                register={form.register("to")}
                disabled
              />
              <TextFormField
                label="CC"
                register={form.register("cc")}
                placeholder="Separate multiple emails with commas"
              />
              <TextFormField
                label="BCC"
                register={form.register("bcc")}
                placeholder="Separate multiple emails with commas"
              />
              <div className="col-span-full flex flex-col gap-2">
                <Typography size="sm" variant="subHeading">
                  Preview
                </Typography>
                <div className="bg-card overflow-hidden rounded-md border shadow-sm">
                  {isSignatureTemplate && signatureTemplateData && (
                    <SignatureRequestEmailPreview
                      templateData={signatureTemplateData}
                    />
                  )}
                  {isShareFolderTemplate && shareFolderTemplateData && (
                    <ShareFolderEmailPreview
                      templateData={shareFolderTemplateData}
                    />
                  )}
                  {!isSignatureTemplate && !isShareFolderTemplate && (
                    <div className="text-muted-foreground p-4 text-sm">
                      Preview unavailable for this template.
                    </div>
                  )}
                </div>
              </div>
            </fieldset>
          </div>
          <div className="flex grow" />
          <SheetFooter addSlideoverStyles>
            <FormCancelButton
              setOpen={setOpen}
              isDirty={form.formState.isDirty}
            />
            <FormSubmitButton
              disabled={false}
              loading={isSubmitting}
              title="Send email"
            />
          </SheetFooter>
        </form>
      </Form>
    </Slideover>
  );
};
